import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Download, CreditCard, Check as CheckIcon, Clock as ClockIcon, XCircle } from "lucide-react";

/**
 * Full transaction history for the logged-in member. Backed by GET /api/payments/history.
 * Members can filter by status and search by reference / description, then click a
 * transaction to see its Stripe receipt when applicable.
 */
export default function PaymentHistoryPage() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | paid | pending | failed
  const [search, setSearch] = useState("");
  const [sp, setSp] = useSearchParams();
  const pollRef = useRef({ tries: 0, timer: null });

  const loadPayments = async () => {
    try {
      const r = await api.get("/payments/history");
      setPayments(r.data || []);
      return r.data || [];
    } catch (e) { console.error("Payment history load failed", e); return []; }
    finally { setLoading(false); }
  };

  useEffect(() => { loadPayments(); }, []);

  // Auto-refresh on Stripe return: if URL carries ?paid=1, toast and re-poll every 2s (up to 30s)
  // until we see a paid tx created in the last 5 minutes — same pattern as the Portal Dashboard.
  useEffect(() => {
    if (sp.get("paid") !== "1") return;
    toast.success("Payment recorded — your ledger is refreshing…");
    setSp(new URLSearchParams(""), { replace: true });
    const poll = async () => {
      const data = await loadPayments();
      const hasNewPaid = data.some((p) => p.payment_status === "paid" && Date.now() - new Date(p.created_at).getTime() < 5 * 60 * 1000);
      const st = pollRef.current;
      st.tries += 1;
      if (!hasNewPaid && st.tries < 15) { st.timer = setTimeout(poll, 2000); }
    };
    pollRef.current.timer = setTimeout(poll, 1500);
    return () => { if (pollRef.current.timer) clearTimeout(pollRef.current.timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  const counts = useMemo(() => ({
    all: payments.length,
    paid: payments.filter((p) => p.payment_status === "paid").length,
    pending: payments.filter((p) => p.payment_status === "pending").length,
    failed: payments.filter((p) => p.payment_status !== "paid" && p.payment_status !== "pending").length,
  }), [payments]);

  const visible = payments.filter((p) => {
    if (filter === "paid" && p.payment_status !== "paid") return false;
    if (filter === "pending" && p.payment_status !== "pending") return false;
    if (filter === "failed" && (p.payment_status === "paid" || p.payment_status === "pending")) return false;
    const q = search.trim().toLowerCase();
    if (q) {
      const hay = `${p.lookup_key || ""} ${p.reference || ""} ${p.method || ""} ${p.property_address || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const totalPaid = visible.filter((p) => p.payment_status === "paid" && p.lookup_key !== "settlement_credit").reduce((s, p) => s + (p.amount || 0), 0);
  const totalCredits = visible.filter((p) => p.lookup_key === "settlement_credit").reduce((s, p) => s + (p.amount || 0), 0);

  const methodLabel = (p) => {
    if (p.lookup_key === "settlement_credit") return "Settlement credit (board applied)";
    if (p.mode === "manual") return (p.method || "manual").replace(/_/g, " ");
    if (p.mode === "subscription_renewal") return "Auto-pay renewal";
    if (p.mode === "subscription") return "Auto-pay";
    return "Credit card · Stripe";
  };

  const isCredit = (p) => p.lookup_key === "settlement_credit";

  const statusBadge = (p) => {
    const s = p.payment_status;
    if (isCredit(p)) return <Badge data-testid={`status-${p.id}`} className="bg-[#C86650] text-white"><CheckIcon className="w-3 h-3 mr-1" />Credit applied</Badge>;
    if (s === "paid") return <Badge data-testid={`status-${p.id}`} className="bg-[#2C4C3B] text-white"><CheckIcon className="w-3 h-3 mr-1" />Paid</Badge>;
    if (s === "pending") return <Badge data-testid={`status-${p.id}`} className="bg-[#EADCC8] text-[#2C4C3B]"><ClockIcon className="w-3 h-3 mr-1" />Pending</Badge>;
    return <Badge data-testid={`status-${p.id}`} className="bg-[#C86650] text-white capitalize"><XCircle className="w-3 h-3 mr-1" />{s || "failed"}</Badge>;
  };

  return (
    <div data-testid="payment-history-page" className="max-w-6xl mx-auto px-6 py-12">
      <Link to="/portal" className="inline-flex items-center gap-1 text-xs text-[#5C6A64] hover:text-[#2C4C3B] mb-4" data-testid="back-to-portal">
        <ArrowLeft className="w-3 h-3" /> Back to portal
      </Link>
      <p className="text-xs tracking-[0.3em] uppercase text-[#C86650] mb-2">Portal</p>
      <h1 className="font-serif text-4xl text-[#1A2421] mb-2">Payment history</h1>
      <p className="text-[#5C6A64] mb-8">Every payment, online or offline, recorded against your account.</p>

      <Card className="rounded-2xl border-[#D5CFC4] mb-6">
        <CardContent className="p-5">
          <div className="flex flex-wrap gap-6">
            <div><p className="text-xs text-[#5C6A64] uppercase tracking-wider">Total paid (filtered)</p><p className="text-2xl font-medium text-[#2C4C3B]" data-testid="total-paid">${(totalPaid / 100).toFixed(2)}</p></div>
            {totalCredits > 0 && (
              <div data-testid="total-credits-block"><p className="text-xs text-[#5C6A64] uppercase tracking-wider">Credits applied</p><p className="text-2xl font-medium text-[#C86650]" data-testid="total-credits">–${(totalCredits / 100).toFixed(2)}</p></div>
            )}
            <div><p className="text-xs text-[#5C6A64] uppercase tracking-wider">Transactions</p><p className="text-2xl font-medium text-[#1A2421]" data-testid="tx-count">{visible.length}</p></div>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-2 items-center mb-4">
        {[
          { key: "all", label: "All", count: counts.all },
          { key: "paid", label: "Paid", count: counts.paid },
          { key: "pending", label: "Pending", count: counts.pending },
          { key: "failed", label: "Failed / canceled", count: counts.failed },
        ].map((c) => (
          <Button
            key={c.key}
            size="sm"
            data-testid={`filter-${c.key}`}
            variant={filter === c.key ? "default" : "outline"}
            onClick={() => setFilter(c.key)}
            className={filter === c.key ? "rounded-full bg-[#2C4C3B] hover:bg-[#1F3D2E] text-white" : "rounded-full border-[#D5CFC4] text-[#1A2421]"}
          >
            {c.label} ({c.count})
          </Button>
        ))}
        <Input
          data-testid="history-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search reference, method…"
          className="w-64 rounded-full bg-white border-[#D5CFC4] ml-auto"
        />
      </div>

      <Card className="rounded-2xl border-[#D5CFC4]">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#EADCC8] text-[#2C4C3B]">
              <tr>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Description</th>
                <th className="text-left px-4 py-3">Method</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Receipt</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="text-center py-8 text-[#5C6A64]">Loading…</td></tr>}
              {!loading && visible.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-[#5C6A64]">No payments match this filter.</td></tr>}
              {!loading && visible.map((p) => (
                <tr key={p.id} data-testid={`history-row-${p.id}`} className="border-t border-[#D5CFC4] hover:bg-[#F9F8F6]">
                  <td className="px-4 py-3 text-[#5C6A64] whitespace-nowrap">
                    <div>{new Date(p.created_at).toLocaleDateString()}</div>
                    <div className="text-[10px] text-[#8A9490]" data-testid={`history-time-${p.id}`}>{new Date(p.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</div>
                  </td>
                  <td className="px-4 py-3 text-[#1A2421]">
                    {(p.lookup_key || "payment").replace(/_/g, " ")}
                    {p.reference && <p className="text-xs text-[#5C6A64]">Ref: {p.reference}</p>}
                  </td>
                  <td className="px-4 py-3 text-[#1A2421]">
                    <span className="inline-flex items-center gap-1">
                      <CreditCard className="w-3 h-3 text-[#5C6A64]" />
                      {methodLabel(p)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-medium">
                    {isCredit(p) ? (
                      <span className="text-[#C86650]" data-testid={`amount-credit-${p.id}`}>–${((p.amount || 0) / 100).toFixed(2)}</span>
                    ) : (
                      <span className="text-[#2C4C3B]">${((p.amount || 0) / 100).toFixed(2)}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">{statusBadge(p)}</td>
                  <td className="px-4 py-3 text-right">
                    {p.payment_status === "paid" && p.receipt_url ? (
                      <a href={p.receipt_url} target="_blank" rel="noreferrer" data-testid={`receipt-${p.id}`} className="text-xs text-[#C86650] underline inline-flex items-center gap-1">
                        <Download className="w-3 h-3" /> Stripe receipt
                      </a>
                    ) : (
                      <span className="text-xs text-[#5C6A64]">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
